import { AsyncPipe } from '@angular/common';
import { Component, HostListener, inject, Input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { BaseProjectDto, ProjectStatus } from 'app/api/backend-connector/models';
import { AuthenticationService } from 'app/services/authentication.service';
import { ProjectService } from 'app/services/project.service';
import { ToastService } from 'app/services/toast.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, TranslateModule, AsyncPipe],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent {
  @Input() projectCode: string = '';
  @Input() projectStatus: string = '';
  @Input() lastUpdate: string = '';
  @Input() projectId: string = '';
  @Input() currentProject: BaseProjectDto | null = null;
  readonly ProjectStatus = ProjectStatus;
  readonly authenticationService = inject(AuthenticationService);
  readonly projectService = inject(ProjectService);
  readonly toastService = inject(ToastService);
  readonly translateService = inject(TranslateService);

  readonly me$ = this.authenticationService.getMe();

  statusDropdownOpen = false;
  userMenuOpen = false;

  readonly statusOptions = [
    { value: ProjectStatus.NotSet, label: 'i18n.projectStatus.notSet' },
    { value: ProjectStatus.OnTrack, label: 'i18n.projectStatus.onTrack' },
    { value: ProjectStatus.OffTrack, label: 'i18n.projectStatus.offTrack' },
    { value: ProjectStatus.NotStarted, label: 'i18n.projectStatus.notStarted' },
    { value: ProjectStatus.Finished, label: 'i18n.projectStatus.finished' },
    { value: ProjectStatus.Discontinued, label: 'i18n.projectStatus.discontinued' },
  ];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInsideDropdown =
      target.closest('.status-dropdown-container') || target.closest('.user-menu-container');

    if (!clickedInsideDropdown) {
      this.statusDropdownOpen = false;
      this.userMenuOpen = false;
    }
  }

  get hasProject(): boolean {
    return !!this.projectCode;
  }

  toggleStatusDropdown(): void {
    this.statusDropdownOpen = !this.statusDropdownOpen;
    this.userMenuOpen = false;
  }

  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
    this.statusDropdownOpen = false;
  }

  async changeProjectStatus(newStatus: ProjectStatus): Promise<void> {
    if (!this.currentProject || !this.projectId) return;

    this.statusDropdownOpen = false;

    try {
      await this.projectService.editProject({
        id: this.projectId,
        body: {
          name: this.currentProject.name,
          description: this.currentProject.description,
          status: newStatus,
          isArchived: this.currentProject.isArchived,
          isPublic: this.currentProject.isPublic,
          allowBudgetCapOvercome: this.currentProject.allowBudgetCapOvercome,
          allowEstimatesOvercome: this.currentProject.allowEstimatesOvercome,
          allowMultipleBudget: this.currentProject.allowMultipleBudget,
          allowNotAllocatedEffectives: this.currentProject.allowNotAllocatedEffectives,
          allowUpdateBudgetVersionEstimates: this.currentProject.allowUpdateBudgetVersionEstimates,
          basicCostTypeIds: [],
          budgetEnabled: this.currentProject.budgetEnabled,
          cashFlowEnabled: this.currentProject.cashFlowEnabled,
          costFlowEnabled: this.currentProject.costFlowEnabled,
          endDate: this.currentProject.endDate,
          startDate: this.currentProject.startDate,
          includeLaborCostType: false,
          laborCostIds: [],
          personEstimates: this.currentProject.personEstimates,
          projectStatusDescription: this.currentProject.projectStatusDescription,
          revenueEnabled: this.currentProject.revenueEnabled,
          workPackageEstimates: this.currentProject.workPackageEstimates,
          workPackageTypeIds: [],
          data: {},
        },
      });
      this.toastService.success(this.translateService.instant('i18n.navbar.statusUpdated'));
      window.location.reload();
    } catch (error) {
      console.error('Error updating project status:', error);
      this.toastService.error(this.translateService.instant('i18n.navbar.statusUpdateError'));
    }
  }

  logout(): void {
    sessionStorage.removeItem('impersonatedTenant');
    void this.authenticationService.logout();
  }
}
